import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "../../components/panel/panel.jsx";
import { useCallback, useEffect, useState } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "./dropdownStyles.js";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import { formatStaffActivityAction } from "../../utils/staffActivityActionLabels.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import UpdateShimmer from "../../components/CustomComponents/preLoad/UpdateShimmer.jsx";
import ImageUpload from "../../components/CustomComponents/ImageUpload/imageUpload";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector.jsx";
import { Modal } from "react-bootstrap";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import moment from "moment";

const ACTIVITY_LOG_PAGE_SIZE = 50;

function UpdateStaff() {
  const { UserName } = useParams();
  const navigate = useNavigate();
  const authState = useSelector((state) => state.auth);
  const CompID = authState.user?.userData?.compID;
  const { profileData } = authState;
  const profileCountry = profileData?.compCountry ?? profileData?.CompCountry ?? "";
  const profileCity = profileData?.compCity ?? profileData?.CompCity ?? "";

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();


  // Select appropriate dropdown styles based on theme
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [staffData, setStaffData] = useState({
    userName: "",
    firstName: "",
    lastName: "",
    email: "",
    country: "",
    city: "",
    phoneNo: "",
    passCode: "",
    ratePerHour: null,
    active: false,
    userRoles: null,
    staffImage: "",
    isAllowOverTime: false,
    isFullTimeStaff: false,
    isNewHireStaff: false,
    overTimeHourlyRate: null,
    isSalaryStaff: false,
    salaryType: "Weekly",
    salary: null,
  });
  const [initialLoading, setInitialLoading] = useState(true);

  // Errors state for real-time validation
  const [errors, setErrors] = useState({});

  // Image state
  const [imageState, setImageState] = useState({
    staffImage: "",
    imageFile: null,
  });

  // Change password modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showActivityLogModal, setShowActivityLogModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmNewPassword: "",
  });

  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogPage, setActivityLogPage] = useState(1);
  const [activityLogTotalRecords, setActivityLogTotalRecords] = useState(0);
  const [activityLogTotalPages, setActivityLogTotalPages] = useState(1);
  const activityLogPageSizeOption = { value: "50", label: "50" };

  const FetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ROLES, {
        params: {
          CompID,
        },
      });
      const roleData = response.data.data || response.data;
      const rolesOptions = roleData.map((role) => ({
        value: role.comRolePermission_ID,
        label: role.r_Title,
      }));
      setRoles(rolesOptions);
    } catch (err) {
      showErrorToast("Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  const FetchStaffData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_STAFF_DATA, {
        params: {
          CompID,
          UserName,
        },
      });
      const staff = response.data.data || response.data;

      const defaultCountry =
        profileCountry ||
        staff.country ||
        "";

      setSelectedRole(
        roles.find((r) => r.value === staff.comRolePermission_ID)
      );

      const defaultCity =
        staff.city ||
        profileCity ||
        "";

      setStaffData({
        userName: staff.userName || "",
        firstName: staff.firstName || "",
        lastName: staff.lastName || "",
        email: staff.email || "",
        country: defaultCountry,
        city: defaultCity,
        phoneNo: staff.phoneNo || "",
        passCode: staff.passCode || "",
        ratePerHour: staff.ratePerHour || null,
        active: staff.active || false,
        userRoles: staff.comRolePermission_ID || null,
        isBreakAllowed: staff.isBreakAllowed || false,
        breakMinutes: staff.breakMinutes || null,
        staffImage: staff.staffImage || "",
        isAllowOverTime: staff.isAllowOverTime || false,
        isFullTimeStaff: staff.isFullTimeStaff || false,
        isNewHireStaff: staff.isNewHireStaff || false,
        overTimeHourlyRate: staff.overTimeHourlyRate || null,
        isSalaryStaff: staff.isSalaryStaff || false,
        salaryType: staff.salaryType || "Weekly",
        salary: staff.salary || null,
      });

      // Set image state
      setImageState({
        staffImage: staff.staffImage || "",
        imageFile: null,
      });
    } catch (err) {
      showErrorToast("Failed to fetch staff data");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [CompID, UserName, roles, profileCountry, profileCity]);

  useEffect(() => {
    FetchRoles();
  }, [FetchRoles]);

  useEffect(() => {
    if (roles.length > 0) {
      FetchStaffData();
    }
  }, [roles, FetchStaffData]);

  useEffect(() => {
    setActivityLogPage(1);
  }, [UserName]);

  const routeStaffId = (() => {
    if (!UserName) return "";
    try {
      return decodeURIComponent(UserName);
    } catch {
      return UserName;
    }
  })();
  const staffIdForLogs = (staffData.userName && String(staffData.userName).trim()) || routeStaffId;

  const fetchActivityLogs = useCallback(async () => {
    if (!CompID || !staffIdForLogs) return;
    try {
      setActivityLogsLoading(true);
      const response = await axiosInstance.get(APIs.GET_STAFF_ACTIVITY_LOGS, {
        params: {
          CompID,
          Staff_ID: staffIdForLogs,
          PageNumber: activityLogPage,
          PageSize: ACTIVITY_LOG_PAGE_SIZE,
        },
      });
      const apiOk = response.data?.status === true || response.data?.Status === true;
      if (!apiOk) {
        showErrorToast(response.data?.message || response.data?.Message || "Failed to load activity logs");
        setActivityLogs([]);
        setActivityLogTotalRecords(0);
        setActivityLogTotalPages(1);
        return;
      }
      const payload = response.data.data || {};
      const rows = payload.data ?? payload.Data ?? [];
      setActivityLogs(Array.isArray(rows) ? rows : []);
      const total = payload.totalRecords ?? payload.TotalRecords ?? 0;
      const tPages = payload.totalPages ?? payload.TotalPages ?? 0;
      setActivityLogTotalRecords(total);
      setActivityLogTotalPages(tPages > 0 ? tPages : 1);
    } catch {
      showErrorToast("Failed to load activity logs");
      setActivityLogs([]);
      setActivityLogTotalRecords(0);
      setActivityLogTotalPages(1);
    } finally {
      setActivityLogsLoading(false);
    }
  }, [CompID, staffIdForLogs, activityLogPage]);

  useEffect(() => {
    if (!showActivityLogModal || !CompID || !staffIdForLogs) return;
    fetchActivityLogs();
  }, [showActivityLogModal, CompID, staffIdForLogs, activityLogPage, fetchActivityLogs]);

  // Validation functions
  const validateFirstName = (value) => {
    if (!value.trim()) return "First name is required";
    if (value.length < 2) return "First name must be at least 2 characters long";
    return "";
  };

  const validateEmail = (value) => {
    if (!value.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    return "";
  };

  const validateCountry = (value) => {
    if (!value) return "Country is required";
    return "";
  };

  const validateCity = (value) => {
    if (!value || !value.toString().trim()) return "City is required";
    return "";
  };

  const validatePassCode = (value) => {
    if (!value.trim()) return "Pass code is required";
    return "";
  };

  const validateRole = (value) => {
    if (!value) return "User role is required";
    return "";
  };

  const validateBreakMinutes = (value, isBreakAllowed) => {
    if (!isBreakAllowed) return "";
    if (!value || value <= 0) return "Break time must be a positive number";
    return "";
  };

  const validateOverTimeRate = (value, isAllowOverTime, ratePerHour) => {
    if (!isAllowOverTime) return "";
    let numValue = value ? parseFloat(value) : null;
    if (numValue === null) {
      numValue = ratePerHour ? parseFloat(ratePerHour) : null;
    }
    if (!numValue || numValue <= 0) return "Overtime rate must be a positive number";
    return "";
  };

  const validateCompensation = (value, isFullTime, isRequired = false) => {
    if (value === null || value === undefined || value === "") {
      if (isRequired) {
        return isFullTime ? "Salary is required" : "Hourly rate is required";
      }
      return "";
    }
    const numericValue = parseFloat(value);
    if (Number.isNaN(numericValue) || numericValue < 0) {
      return isFullTime ? "Enter a valid monthly salary" : "Enter a valid hourly rate";
    }

    return "";
  };

  const validateSalary = (value, isSalaryStaff) => {
    if (!isSalaryStaff) return "";
    if (!value || parseFloat(value) <= 0)
      return "Salary must be a positive number";
    return "";
  };

  const validateSalaryType = (value, isSalaryStaff) => {
    if (!isSalaryStaff) return "";
    if (!value || value.trim() === "")
      return "Salary type is required";
    return "";
  };

  // Generic onChange handler with validation
  const handleInputChange = (field, value, validator, extraValidator = null) => {
    setStaffData(prev => ({ ...prev, [field]: value }));

    // Clear previous error
    setErrors(prev => ({ ...prev, [field]: "" }));

    // Validate immediately
    let error = validator ? validator(value) : "";
    if (extraValidator) {
      const extraError = extraValidator(value);
      if (extraError) error = extraError;
    }

    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  // On blur validation for selects (since they don't have direct onChange for value)
  const handleSelectChange = (field, value, validator) => {
    let error = validator ? validator(value) : "";
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleCountryCityChange = (updatedValues) => {
    setStaffData((prev) => ({
      ...prev,
      country: prev.country || profileData?.compCountry || profileData?.CompCountry || "",
      city: updatedValues.city || "",
    }));

    if (updatedValues.city !== undefined) {
      setErrors((prev) => ({
        ...prev,
        city: validateCity(updatedValues.city || ""),
      }));
    }
  };

  // Overall form validation for submit
  const validateForm = () => {
    const newErrors = {};

    // Trigger all validations
    newErrors.firstName = validateFirstName(staffData.firstName);
    newErrors.email = validateEmail(staffData.email);
    newErrors.country = validateCountry(staffData.country);
    newErrors.city = validateCity(staffData.city);
    newErrors.passCode = validatePassCode(staffData.passCode);
    newErrors.userRoles = validateRole(selectedRole?.value);

    newErrors.breakMinutes = validateBreakMinutes(staffData.breakMinutes, staffData.isBreakAllowed);

    // Status & Salary Info: only validate fields that are visible / applicable
    if (staffData.isSalaryStaff && staffData.salaryType === "Hourly") {
      newErrors.ratePerHour = validateCompensation(
        staffData.ratePerHour,
        false,
        true
      );
    }
    if (staffData.isSalaryStaff) {
      newErrors.salary = validateSalary(staffData.salary, true);
      newErrors.salaryType = validateSalaryType(staffData.salaryType, true);
    }
    if (staffData.isAllowOverTime) {
      newErrors.overTimeHourlyRate = validateOverTimeRate(
        staffData.overTimeHourlyRate,
        true,
        staffData.ratePerHour
      );
    }

    setErrors(newErrors);

    // Check if any errors
    const hasErrors = Object.values(newErrors).some(
      (error) => typeof error === "string" && error !== ""
    );
    if (hasErrors) {
      // Show a general toast if needed, but fields already show errors
      showErrorToast("Please fix the errors below before submitting");
      return false;
    }

    return true;
  };

  // Handler for image changes from ImageUpload component
  const handleImageChange = (url, file) => {
    setImageState({
      staffImage: url,
      imageFile: file,
    });
  };

  // Handler for removing image
  const handleRemoveImage = () => {
    setImageState({
      staffImage: "",
      imageFile: null,
    });
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    let {
      userName,
      firstName,
      lastName,
      email,
      country,
      city,
      phoneNo,
      passCode,
      ratePerHour,
      active,
      isBreakAllowed,
      breakMinutes,
      isAllowOverTime,
      isFullTimeStaff,
      isNewHireStaff,
      overTimeHourlyRate,
      isSalaryStaff,
      salaryType,
      salary,
    } = staffData;

    // Enforce business rules before submit
    if (isAllowOverTime && (!overTimeHourlyRate || overTimeHourlyRate === null)) {
      overTimeHourlyRate = ratePerHour ? parseFloat(ratePerHour) : 0;
    }

    if (!country) {
      country =
        profileData?.compCountry ||
        profileData?.CompCountry ||
        "";
    }

    if (!city) {
      city =
        profileData?.compCity ||
        profileData?.CompCity ||
        "";
    }

    try {
      setLoading(true);

      let finalStaffImage = null;

      if (imageState.imageFile) {
        // Upload new image
        const imageFormData = new FormData();
        imageFormData.append("ImageFile", imageState.imageFile);
        imageFormData.append("UserName", userName);
        imageFormData.append("CompID", CompID);

        const imageResponse = await axiosInstance.post(
          APIs.UPDATE_STAFF_IMAGE,
          imageFormData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            params: {
              UserName: userName,
              CompID: CompID,
            },
          }
        );
        finalStaffImage = imageResponse.data?.data?.filePath || null;

        if (finalStaffImage) {
          const displayUrl = `${finalStaffImage}?t=${Date.now()}`;
          setImageState({ staffImage: displayUrl, imageFile: null });
        }
      } else if (!imageState.staffImage || imageState.staffImage === "") {
        // Image was removed
        finalStaffImage = null;
      } else {
        // Keep existing image (remove query params)
        finalStaffImage = imageState.staffImage.split("?")[0];
      }

      const isHourlySalary =
        isSalaryStaff && salaryType === "Hourly";

      const response = await axiosInstance.put(APIs.UPDATE_STAFF, {
        UserName: userName,
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        Country: country,
        City: city,
        PhoneNo: phoneNo,
        PassCode: passCode,
        RatePerHour:
          isHourlySalary && ratePerHour !== null
            ? parseFloat(ratePerHour)
            : null,
        Active: active,
        Password: "",
        CompID: CompID,
        ComRolePermission_ID: selectedRole?.value || null,
        ManagerUserName: "",
        IsBreakAllowed: isBreakAllowed,
        BreakMinutes: breakMinutes ? parseInt(breakMinutes) : null,
        StaffImage: finalStaffImage || null,
        IsAllowOverTime: isAllowOverTime,
        ISFullTimeStaff: true,
        ISNewHireStaff: isNewHireStaff,
        OverTimeHourlyRate: overTimeHourlyRate ? parseFloat(overTimeHourlyRate) : 0,
        IsSalaryStaff: isSalaryStaff,
        SalaryType: isSalaryStaff ? salaryType : "",
        Salary:
          isSalaryStaff &&
          salaryType !== "Hourly" &&
          salary !== null
            ? parseFloat(salary)
            : null,
      });

      setLoading(false);

      if (response.data.status) {
        showSuccessToast("Staff updated successfully!");
        navigate("/team/manageTeam");
      } else {
        showErrorToast(response.data.message || "Failed to update staff");
      }
    } catch (error) {
      setLoading(false);
      const errorMessage =
        error.response?.data?.message ||
        (Array.isArray(error.response?.data?.errors)
          ? error.response.data.errors.join(", ")
          : "An error occurred while updating the staff");
      showErrorToast(errorMessage);
    }
  };

  const cardStyle = {
    backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
    border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6",
    color: isDarkMode ? "#ffffff" : "#000000",
    borderRadius: "12px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
    transition: "all 0.3s ease",
  };

  const sectionHeaderStyle = {
    color: isDarkMode ? "#ffffff" : "#495057",
    fontSize: "1.1rem",
    fontWeight: "600",
    marginBottom: "1.5rem",
    paddingBottom: "0.5rem",
    borderBottom: `2px solid ${themeColor}`,
  };

  const removeButtonStyle = {
    backgroundColor: isDarkMode ? "#dc3545" : "#dc3545",
    border: "none",
    color: "white",
    borderRadius: "6px",
    padding: "6px 12px",
    fontSize: "14px",
    transition: "all 0.3s ease",
  };

  // Error message style
  const errorStyle = {
    color: '#dc3545',
    fontSize: '0.875rem',
    marginTop: '0.25rem',
    fontWeight: '400',
  }

  const rateFieldLabel = "Hourly Rate";
  const rateFieldPlaceholder = "Enter Hourly Rate";

  // Handle password change
  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmNewPassword) {
      showErrorToast("Passwords do not match.");
      return;
    }

    if (!passwordData.newPassword || passwordData.newPassword.trim() === "") {
      showErrorToast("Password cannot be empty.");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.UPDATE_PASSWORD, {
        UserName,
        Password: passwordData.newPassword,
        ConfirmPassword: passwordData.newPassword
      }, {
        params: {
          CompID
        },
      });
      if (response.data.status) {
        showSuccessToast("Password updated successfully.");
        setShowPasswordModal(false);
        setPasswordData({ newPassword: "", confirmNewPassword: "" });
      } else {
        showErrorToast(response.data.message);
      }
    } catch (err) {
      showErrorToast("Failed to update password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && !initialLoading && <Preloader />}
      <div className="d-flex align-items-center mb-3 mt-2">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/team/manageTeam">Manage Staff</Link>
            </li>
            <li className="breadcrumb-item active">Update Staff</li>
          </ol>
          <h1 className="page-header mb-0">Update Staff ({UserName})</h1>
        </div>
        <div className="ms-auto d-flex gap-2">
          <button
            onClick={() => navigate(`/team/teamSchedule/${UserName}`)}
            className="btn"
            style={{
              backgroundColor: "#6c757d",
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "10px 16px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              cursor: "pointer",
            }}
            title="Staff Schedule"
          >
            <i className="fa fa-calendar fa-lg"></i>
          </button>
          <button
            onClick={() => setShowPasswordModal(true)}
            className="btn"
            style={{
              backgroundColor: "#ffc107",
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "10px 16px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              cursor: "pointer",
            }}
            title="Change Password"
          >
            <i className="fa fa-key fa-lg"></i>
          </button>
          <button
            type="button"
            onClick={() => {
              setActivityLogPage(1);
              setShowActivityLogModal(true);
            }}
            className="btn"
            style={{
              backgroundColor: "#17a2b8",
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "10px 16px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              cursor: "pointer",
            }}
            title="Activity log"
          >
            <i className="fa fa-history fa-lg"></i>
          </button>
          <button
            onClick={handleSubmit}
            className="btn px-4"
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "10px 20px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: `0 2px 8px ${themeColor}40`,
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }}
          >
            <i className="fa fa-save fa-lg me-2 ms-n2 text-white"></i>
            Save
          </button>
        </div>
      </div>
      <div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>Staff Information</PanelHeader>
          <PanelBody>
            {initialLoading ? (
              <UpdateShimmer />
            ) : (
              <div className="row g-4">
                {/* Basic Information */}
                <div className="col-md-6">
                  <div className="p-4" style={cardStyle}>

                    <div className="row g-3">
                      <div className="col-12">
                        <FormInput
                          label="First Name *"
                          placeHolder="Enter First Name"
                          type="text"
                          value={staffData.firstName}
                          onchange={(e) =>
                            handleInputChange("firstName", e.target.value, validateFirstName)
                          }
                        />
                        {errors.firstName && <div style={errorStyle}>{errors.firstName}</div>}
                      </div>
                      <div className="col-12">
                        <FormInput
                          label="Last Name"
                          placeHolder="Enter Last Name"
                          type="text"
                          value={staffData.lastName}
                          onchange={(e) =>
                            setStaffData({
                              ...staffData,
                              lastName: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="col-12">
                        <FormInput
                          label="Email"
                          placeHolder="Enter Email"
                          type="email"
                          value={staffData.email}
                          onchange={(e) =>
                            handleInputChange("email", e.target.value, validateEmail)
                          }
                        />
                        {errors.email && <div style={errorStyle}>{errors.email}</div>}
                      </div>
                      <div className="col-12">
                        <FormInput
                          label="Cell Number"
                          placeHolder="Enter Cell Number"
                          type="text"
                          value={staffData.phoneNo}
                          onchange={(e) =>
                            setStaffData({
                              ...staffData,
                              phoneNo: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Image + Location & Role */}
                <div className="col-md-6">
                  <div className="p-4 d-flex flex-column gap-3" style={cardStyle}>
                    <div className="text-center">
                      <ImageUpload
                        imageUrl={imageState.staffImage}
                        imageFile={imageState.imageFile}
                        setImage={handleImageChange}
                        className=""
                        centerButton={true}
                        useGrid={false}
                      />
                    </div>
                    <div>

                      <div className="row g-3">
                        <div className="col-md-6 col-12">
                          <CountryCitySelector
                            selectedCountry={
                              staffData.country ||
                              profileData?.compCountry ||
                              profileData?.CompCountry ||
                              ""
                            }
                            selectedCity={staffData.city}
                            setSelectedCity={(cityValue) => {
                              const value = cityValue || "";
                              setStaffData((prev) => ({ ...prev, city: value }));
                              setErrors((prev) => ({
                                ...prev,
                                city: validateCity(value),
                              }));
                            }}
                            onChange={handleCountryCityChange}
                            setLoading={null}
                            showCountryField={false}
                            showCityField={true}
                            cityLabel="City *"
                            cityPlaceholder="Select City"
                            isDarkMode={isDarkMode}
                            selectedValueIsLabel={true}
                          />
                          {errors.city && <div style={errorStyle}>{errors.city}</div>}
                        </div>
                        <div className="col-md-6 col-12">
                          <label
                            className={
                              isDarkMode ? "text-white" : "text-gray-700"
                            }
                            style={{ fontWeight: "500" }}
                          >
                            User Roles *
                          </label>
                          <Select
                            value={selectedRole}
                            onChange={(option) => {
                              const newValue = option ? option.value : null;
                              setSelectedRole(option);
                              setStaffData({
                                ...staffData,
                                userRoles: newValue,
                              });
                              handleSelectChange("userRoles", newValue, validateRole);
                            }}
                            options={roles}
                            styles={dropdownStyles}
                            placeholder="Select Role"
                            isClearable={true}
                          />
                          {errors.userRoles && <div style={errorStyle}>{errors.userRoles}</div>}
                        </div>
                        <div className="col-12">
                          <FormInput
                            label="PIN Code *"
                            placeHolder="Enter PIN Code"
                            type="text"
                            value={staffData.passCode}
                            onchange={(e) =>
                              handleInputChange("passCode", e.target.value, validatePassCode)
                            }
                          />
                          {errors.passCode && <div style={errorStyle}>{errors.passCode}</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status & Compensation */}
                <div className="col-12">
                  <div className="p-4" style={cardStyle}>
                    <h6 style={sectionHeaderStyle}>Status & Salary Info</h6>
                    <div className="row g-3 align-items-center mb-2">
                      {/* Row 1: Enable Staff */}
                      <div className="col-md-4 col-sm-6 col-12">
                        <ToggleSwitch
                          isChecked={staffData.active}
                          onToggle={(checked) =>
                            setStaffData({ ...staffData, active: checked })
                          }
                          label="Enable Staff"
                          name="active"
                          themeColor={themeColor}
                        />
                      </div>
                    </div>
                    
                    <div className="row g-3 align-items-center mb-2">
                      {/* Row 2: Salary Staff | Hourly Rate (if not salary staff) OR Salary Type & Salary (if salary staff) */}
                      <div className="col-md-4 col-sm-6 col-12">
                        <ToggleSwitch
                          isChecked={staffData.isSalaryStaff}
                          onToggle={(checked) => {
                            setStaffData({
                              ...staffData,
                              isSalaryStaff: checked,
                              ratePerHour: checked ? staffData.ratePerHour : null,
                              salary: checked ? staffData.salary : null,
                              salaryType: checked ? staffData.salaryType : "Weekly",
                            });
                            setErrors((prev) => ({
                              ...prev,
                              ratePerHour: "",
                              salary: "",
                              salaryType: "",
                            }));
                          }}
                          label="Salary Staff"
                          name="salaryStaff"
                          themeColor={themeColor}
                        />
                      </div>
                      {/* Salary Type - shown when Salary Staff is enabled */}
                      {staffData.isSalaryStaff && (
                        <div className="col-md-4 col-sm-6 col-12">
                          <label
                            className={isDarkMode ? "text-white" : "text-gray-700"}
                            style={{ fontWeight: "500" }}
                          >
                            Salary Type *
                          </label>
                          <Select
                            value={
                              staffData.salaryType
                                ? { value: staffData.salaryType, label: staffData.salaryType }
                                : null
                            }
                            onChange={(option) => {
                              const newValue = option ? option.value : "Weekly";
                              setStaffData({ ...staffData, salaryType: newValue });
                              handleSelectChange(
                                "salaryType",
                                newValue,
                                (val) => validateSalaryType(val, staffData.isSalaryStaff)
                              );
                            }}
                            options={[
                              { value: "Weekly", label: "Weekly" },
                              { value: "Monthly", label: "Monthly" },
                              { value: "Hourly", label: "Hourly" },
                            ]}
                            styles={dropdownStyles}
                            placeholder="Select Salary Type"
                            isClearable={false}
                          />
                          {errors.salaryType && (
                            <div style={errorStyle}>{errors.salaryType}</div>
                          )}
                        </div>
                      )}
                      {/* Hourly Rate - shown when Salary Type is Hourly */}
                      {staffData.isSalaryStaff &&
                        staffData.salaryType === "Hourly" && (
                        <div className="col-md-4 col-sm-6 col-12">
                          <FormInput
                            label={rateFieldLabel}
                            placeHolder={rateFieldPlaceholder}
                            type="number"
                            step="0.01"
                            value={
                              staffData.ratePerHour !== null && staffData.ratePerHour !== undefined
                                ? staffData.ratePerHour
                                : ""
                            }
                            onchange={(e) => {
                              const rawValue = e.target.value;
                              const parsedValue = rawValue
                                ? parseFloat(rawValue)
                                : null;
                              setStaffData((prev) => {
                                const updated = {
                                  ...prev,
                                  ratePerHour: parsedValue,
                                };
                                setErrors((prevErrors) => ({
                                  ...prevErrors,
                                  ratePerHour: validateCompensation(
                                    rawValue,
                                    false
                                  ),
                                }));
                                return updated;
                              });
                            }}
                          />
                        </div>
                      )}
                      {/* Salary Field - shown when Salary Staff is enabled */}
                      {staffData.isSalaryStaff &&
                        staffData.salaryType !== "Hourly" && (
                        <div className="col-md-4 col-sm-6 col-12">
                          <FormInput
                            label={`${staffData.salaryType} Salary *`}
                            placeHolder={`Enter ${staffData.salaryType} Salary`}
                            type="number"
                            step="0.01"
                            value={staffData.salary || ""}
                            onchange={(e) =>
                              handleInputChange(
                                "salary",
                                e.target.value ? parseFloat(e.target.value) : null,
                                null,
                                () => validateSalary(
                                  e.target.value ? parseFloat(e.target.value) : null,
                                  staffData.isSalaryStaff
                                )
                              )
                            }
                          />
                          {errors.salary && (
                            <div style={errorStyle}>{errors.salary}</div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="row g-3 align-items-center">
                      {/* Row 3: Enable Extra Hours | Overtime Rate */}
                      <div className="col-md-4 col-sm-6 col-12">
                        <ToggleSwitch
                          isChecked={staffData.isAllowOverTime}
                          onToggle={(checked) => {
                            setStaffData({
                              ...staffData,
                              isAllowOverTime: checked,
                              overTimeHourlyRate: checked ? staffData.overTimeHourlyRate || null : null,
                            });
                            if (!checked) {
                              setErrors((prev) => ({
                                ...prev,
                                overTimeHourlyRate: "",
                              }));
                            }
                          }}
                          label="Enable Extra Hours"
                          name="allowOvertime"
                          description="Enable extra hours"
                          themeColor={themeColor}
                        />
                      </div>
                      {/* Overtime Rate - shown when Extra Hours is enabled */}
                      {staffData.isAllowOverTime && (
                        <div className="col-md-4 col-sm-6 col-12">
                          <FormInput
                            label="Hourly Rate for Extra Hours"
                            placeHolder="Enter Hourly Rate for Extra Hours"
                            type="number"
                            step="0.01"
                            value={staffData.overTimeHourlyRate || ""}
                            onchange={(e) =>
                              handleInputChange(
                                "overTimeHourlyRate",
                                e.target.value ? parseFloat(e.target.value) : null,
                                null,
                                () =>
                                  validateOverTimeRate(
                                    e.target.value ? parseFloat(e.target.value) : null,
                                    staffData.isAllowOverTime,
                                    staffData.ratePerHour
                                  )
                              )
                            }
                          />
                          {errors.overTimeHourlyRate && (
                            <div style={errorStyle}>{errors.overTimeHourlyRate}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </PanelBody>
        </Panel>
      </div>

      {/* Change Password Modal */}
      <Modal
        show={showPasswordModal}
        onHide={() => {
          setShowPasswordModal(false);
          setPasswordData({ newPassword: "", confirmNewPassword: "" });
        }}
        centered
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
            color: isDarkMode ? "#ffffff" : "#000000",
            borderBottom: `2px solid ${themeColor}`,
          }}
        >
          <Modal.Title>
            <i className="fa fa-key me-2" style={{ color: themeColor }}></i>
            Change Password
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
            color: isDarkMode ? "#ffffff" : "#000000",
          }}
        >
          <div className="mb-3">
            <FormInput
              label="New Password"
              placeHolder="Enter New Password"
              type="password"
              value={passwordData.newPassword}
              onchange={(e) =>
                setPasswordData({
                  ...passwordData,
                  newPassword: e.target.value,
                })
              }
            />
          </div>
          <div className="mb-3">
            <FormInput
              label="Confirm New Password"
              placeHolder="Confirm New Password"
              type="password"
              value={passwordData.confirmNewPassword}
              onchange={(e) =>
                setPasswordData({
                  ...passwordData,
                  confirmNewPassword: e.target.value,
                })
              }
            />
          </div>
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
            borderTop: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
          }}
        >
          <button
            className="btn"
            onClick={() => {
              setShowPasswordModal(false);
              setPasswordData({ newPassword: "", confirmNewPassword: "" });
            }}
            style={{
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "8px 20px",
            }}
          >
            Cancel
          </button>
          <button
            className="btn"
            onClick={handlePasswordChange}
            style={{
              backgroundColor: themeColor,
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "8px 20px",
            }}
          >
            <i className="fa fa-check me-2"></i>
            Update Password
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showActivityLogModal}
        onHide={() => setShowActivityLogModal(false)}
        centered
        size="lg"
        scrollable
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
            color: isDarkMode ? "#ffffff" : "#000000",
            borderBottom: `2px solid ${themeColor}`,
          }}
        >
          <Modal.Title>
            <i className="fa fa-history me-2" style={{ color: themeColor }}></i>
            Activity log
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
            color: isDarkMode ? "#ffffff" : "#000000",
            maxHeight: "70vh",
          }}
        >
          {activityLogsLoading ? (
            <div className="py-4 text-center">
              <Preloader />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover table-sm">
                  <thead>
                    <tr>
                      <th>Date / time</th>
                      <th>Action</th>
                      <th>Success</th>
                      <th>Role</th>
                      <th>Device</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityLogs.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          No activity logs found for this staff member.
                        </td>
                      </tr>
                    ) : (
                      activityLogs.map((row) => {
                        const created =
                          row.createdDate ?? row.CreatedDate
                            ? moment(row.createdDate ?? row.CreatedDate).format("YYYY-MM-DD HH:mm")
                            : "";
                        const err = (row.errorMessage ?? row.ErrorMessage ?? "").toString();
                        const errShort = err.length > 120 ? `${err.slice(0, 120)}…` : err;
                        return (
                          <tr key={String(row.activityLog_ID ?? row.ActivityLog_ID ?? `${created}-${row.actionType ?? row.ActionType}`)}>
                            <td>{created}</td>
                            <td>{formatStaffActivityAction(row.actionType ?? row.ActionType ?? "")}</td>
                            <td>{(row.isSuccess ?? row.IsSuccess) ? "Yes" : "No"}</td>
                            <td>{row.roleName ?? row.RoleName ?? ""}</td>
                            <td className="small">{row.deviceInfo ?? row.DeviceInfo ?? ""}</td>
                            <td className="small text-danger">{errShort}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {activityLogTotalRecords > 0 && (
                <Pagination
                  totalRecords={activityLogTotalRecords}
                  pageSize={activityLogPageSizeOption}
                  pageNumber={activityLogPage}
                  totalPages={activityLogTotalPages}
                  setPageNumber={setActivityLogPage}
                  setPageSize={() => {}}
                  themeColor={themeColor}
                />
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
            borderTop: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
          }}
        >
          <button
            type="button"
            className="btn"
            onClick={() => setShowActivityLogModal(false)}
            style={{
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "8px 20px",
            }}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default UpdateStaff;